import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Calendar, FileText, AlertCircle, Clock, MapPin, Phone, Mail, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const PatientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myReferrals, setMyReferrals] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyData();
  }, []);

  const fetchMyData = async () => {
    try {
      // Fetch only MY referrals where I'm the patient
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/my-referrals`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setMyReferrals(data.referrals || []);
      }
    } catch (error) {
      console.error('Error fetching my data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
      in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your information...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Welcome Banner */}
      <Card className="mb-8 bg-gradient-to-r from-[#0A1628] to-[#1a2332] text-white">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-center space-x-4">
            <Avatar className="h-16 w-16 border-4 border-[#D4AF37]">
              <AvatarFallback className="bg-[#D4AF37] text-[#0A1628] text-2xl font-bold">
                {user?.name?.charAt(0) || 'P'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl font-bold mb-1">Welcome, {user?.name}!</h1>
              <p className="text-gray-300">Here's your personal health journey with ReferBridge</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Patient Stats - ONLY MY DATA */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">My Referrals</p>
                <p className="text-2xl font-bold text-[#0A1628]">{myReferrals.length}</p>
              </div>
              <FileText className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-[#0A1628]">
                  {myReferrals.filter(r => r.status === 'pending').length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Upcoming</p>
                <p className="text-2xl font-bold text-[#0A1628]">
                  {myReferrals.filter(r => r.status === 'scheduled').length}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-[#0A1628]">
                  {myReferrals.filter(r => r.status === 'completed').length}
                </p>
              </div>
              <FileText className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Patient Journey Timeline */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-[#D4AF37]" />
            My Treatment Journey
          </CardTitle>
        </CardHeader>
        <CardContent>
          {myReferrals.length > 0 ? (
            <div className="space-y-4">
              {myReferrals.map((referral, index) => (
                <div key={referral.id} className="flex items-start space-x-4 p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#0A1628] flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="font-semibold text-[#0A1628]">{referral.procedure}</h3>
                      <Badge className={getStatusColor(referral.status)}>
                        {referral.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">
                      Referred by {referral.referring_doctor_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      <Calendar className="h-3 w-3 inline mr-1" />
                      {new Date(referral.created_at).toLocaleDateString()}
                    </p>
                    {referral.scheduled_date && (
                      <p className="text-sm text-[#D4AF37] mt-2">
                        <Clock className="h-3 w-3 inline mr-1" />
                        Appointment: {new Date(referral.scheduled_date).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => navigate(`/referrals/${referral.id}`)}
                  >
                    View Details
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600 mb-2">No referrals yet</p>
              <p className="text-sm text-gray-500">Your referrals will appear here once your doctor submits them</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button className="w-full" variant="outline" onClick={() => navigate('/documents')}>
              <FileText className="h-4 w-4 mr-2" />
              View My Documents
            </Button>
            <Button className="w-full" variant="outline" onClick={() => navigate('/appointments')}>
              <Calendar className="h-4 w-4 mr-2" />
              My Appointments
            </Button>
            <Button className="w-full" variant="outline" onClick={() => navigate('/education')}>
              <User className="h-4 w-4 mr-2" />
              Patient Education
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>My Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center text-sm">
              <Mail className="h-4 w-4 mr-2 text-gray-400" />
              <span className="text-gray-700">{user?.email}</span>
            </div>
            <div className="flex items-center text-sm">
              <User className="h-4 w-4 mr-2 text-gray-400" />
              <span className="text-gray-700">Patient Account</span>
            </div>
            <Button className="w-full mt-4" variant="outline" onClick={() => navigate('/settings')}>
              Update My Information
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PatientDashboard;